import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useTranslation } from "react-i18next";
import { usePermitted } from "../../../../common/components/permissions/permissions";
import Modal from "react-bootstrap/Modal";
import {
  openMarkets,
  closeMarkets,
} from "../../../../common/services/tradingEngine/marketStateService";
import { toast } from "react-toastify";
import ExpandedSideNav from "./ExpandedSideNav";
import CollapsedSideNav from "./CollapsedSideNav";
import buildNavigationModel, { deriveNavigationState } from "./navigationModel";
import NavigationToggleButton from "./NavigationToggleButton";
import useNavigationMenu from "./useNavigationMenu";
import { MenuStateManager } from "../../Menu";
import About from "./../../About/About";
import {
  ENABLE_TRADING,
  ENABLE_MARKET_DATA,
  ENABLE_ALBERTA_ENERGY_DAILY,
  ENABLE_ALBERTA_ENVIRONMENTAL,
  ENABLE_BC_ENVIRONMENTAL,
} from "../../../../common/services/app/featureFlags";
import "../styles/Sidebar.css";

const SIDEBAR_STORAGE_KEY_PREFIX = "userSidebar_v1";
const getSidebarStorageKey = (userAccountId, login) => {
  const storageId = userAccountId ?? login;
  return storageId ? `${SIDEBAR_STORAGE_KEY_PREFIX}_${storageId}` : null;
};

const getStoredSidebarExpanded = (storageKey, fallbackValue) => {
  if (!storageKey) {
    return fallbackValue;
  }

  try {
    const savedValue = localStorage.getItem(storageKey);

    if (savedValue === null) {
      return fallbackValue;
    }

    return savedValue === "true";
  } catch (error) {
    console.warn("Failed to read saved sidebar state:", error);
    return fallbackValue;
  }
};

// Load all images from the folder
const imagesContext = require.context(
  "../../../../config/images", // relative path to folder
  false, // do not include subfolders
  /\.(png|jpe?g|svg)$/, // match image extensions
);

const getImage = (fileName) => {
  try {
    return imagesContext(`./${fileName}`);
  } catch (err) {
    console.error(`Image not found: ${fileName}`);
    return null;
  }
};

const LogoPath = process.env.REACT_APP_LOGO;
const IconLogoPath = process.env.REACT_APP_ICON_LOGO;

const Logo = getImage(LogoPath);
const IconLogo = getImage(IconLogoPath);
const NeutralLogo = getImage("Neutral Markets Logo.png");

const showMarketActionErrors = (ex, t) => {
  console.log(t("ErrorSaving"), ex);

  if (ex.response && ex.response.status === 400) {
    const errorList = ex.response.data.errors;

    if (errorList) {
      const errorKeys = Object.keys(ex.response.data.errors);
      errorKeys.forEach((key) => {
        errorList[key].map((error) => {
          toast.error(t("ErrorSaving") + " " + key + ": " + error);
          return null;
        });
      });
    } else {
      toast.error(t("ErrorSaving") + " " + ex.response.data);
    }
  }
};

// Shared wrapper that wires navigation state into either the expanded or collapsed layout.
const SideNavContent = ({
  t,
  isExpanded,
  isSecondaryRailOpen,
  setIsSecondaryRailOpen,
  expandedLogoSource,
  navigationToggleLabel,
  toggleSidebar,
  handleNavLinkClick,
  handleOpenFlexTab,
  activeMenuItems,
  topLevelItems,
  utilityItems,
  iconLogo,
  isMobile,
  expandedMenus,
  handleCollapseOthers,
  startNavigationSearchSession,
  commitNavigationSearchResult,
  endNavigationSearchSession,
  syncAutoExpandedMenus,
  toggleMenu,
}) => {
  const {
    navigationSearch,
    setNavigationSearch,
    isSearching,
    renderNavigationDescriptor,
    filterNavigationDescriptors,
    filterNavigationGroups,
    syncAutoExpandedMenuIds,
  } = useNavigationMenu({
    expandedMenus,
    startNavigationSearchSession,
    commitNavigationSearchResult,
    endNavigationSearchSession,
    syncAutoExpandedMenus,
    onMenuToggle: toggleMenu,
    handleNavLinkClick,
    handleOpenFlexTab,
    handleCollapseOthers,
    activeMenuItems,
  });

  return (
    <nav
      className={`sidebar-nav ${
        isExpanded ? "expanded sidebar-detail-pane" : "collapsed dual-rail"
      } ${
        !isExpanded && isSecondaryRailOpen ? "sidebar-nav--secondary-open" : ""
      }`.trim()}
    >
      {isExpanded ? (
        <>
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <Link to="/">
                <img
                  src={expandedLogoSource}
                  alt="Neutral Markets"
                  className="full-logo"
                />
              </Link>
            </div>
            <NavigationToggleButton
              icon="chevron-left"
              label={navigationToggleLabel}
              onClick={toggleSidebar}
            />
          </div>
          <ExpandedSideNav
            navigationSearch={navigationSearch}
            setNavigationSearch={setNavigationSearch}
            isSearching={isSearching}
            topLevelItems={topLevelItems}
            utilityItems={utilityItems}
            renderNavigationDescriptor={renderNavigationDescriptor}
            filterNavigationDescriptors={filterNavigationDescriptors}
            syncAutoExpandedMenuIds={syncAutoExpandedMenuIds}
          />
        </>
      ) : (
        <CollapsedSideNav
          handleNavLinkClick={handleNavLinkClick}
          handleOpenFlexTab={handleOpenFlexTab}
          onToggleSidebar={toggleSidebar}
          activeMenuItems={activeMenuItems}
          isMobile={isMobile}
          iconLogo={iconLogo}
          topLevelItems={topLevelItems}
          utilityItems={utilityItems}
          navigationSearch={navigationSearch}
          setNavigationSearch={setNavigationSearch}
          isSearching={isSearching}
          renderNavigationDescriptor={renderNavigationDescriptor}
          filterNavigationGroups={filterNavigationGroups}
          syncAutoExpandedMenuIds={syncAutoExpandedMenuIds}
          onSecondaryRailVisibilityChange={setIsSecondaryRailOpen}
        />
      )}
    </nav>
  );
};

// Top-level navigation orchestrator that builds the model, derives route state, and selects the active layout.
const SideNav = ({
  unapprovedMarkCount,
  unapprovedTradeCount,
  handleLogout,
  onMenuClick,
  userAccountId,
  subscribedMarkets,
  termSchedules,
  manageExternalMarkets,
}) => {
  const location = useLocation();
  const { keycloak, initialized } = useKeycloak();
  const { t } = useTranslation();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSecondaryRailOpen, setIsSecondaryRailOpen] = useState(false);

  // Permissions
  const isAdmin = usePermitted(["administrator", "operator"]);
  const isApiManageMarks = usePermitted(["api-manage-marks"]);
  const isApiManageTrades = usePermitted(["api-manage-trades"]);
  const isApiManageExternalData = usePermitted(["api-manage-external-data"]);
  const isApiApproveMarks = usePermitted(["api-approve-marks"]);
  const isApiApproveTrades = usePermitted(["api-approve-trades"]);
  const isTrader = usePermitted([
    "administrator",
    "trader",
    "trading-company-administrator",
  ]);
  const isTraderAdmin = usePermitted(["administrator", "operator"]);
  const canQueryOwnCompanyWhitelists = usePermitted([
    "api-query-own-company-whitelists",
    "api-manage-own-company-whitelists",
  ]);
  const canQueryCompanyWhitelists = usePermitted([
    "api-query-company-whitelists",
    "api-manage-company-whitelists",
  ]);
  const canManageOrderReleaseLimits = usePermitted([
    "api-manage-order-release-limits",
    "api-manage-own-order-release-limits",
  ]);

  const permissions = React.useMemo(
    () => ({
      authenticated: initialized && keycloak.authenticated,
      showAdmin: isAdmin,
      showUserAdmin: isAdmin,
      showProductAdmin: isAdmin,
      showMarketAdmin: isAdmin,
      showSubmitData:
        isApiManageMarks || isApiManageTrades || isApiManageExternalData,
      showReviewMarks: isApiApproveMarks,
      showReviewTrades: isApiApproveTrades,
      tradingEnabled: ENABLE_TRADING,
      showTrading: ENABLE_TRADING && (isTrader || isTraderAdmin),
      showTradingAdmin: ENABLE_TRADING && isTraderAdmin,
      showMarketData: ENABLE_MARKET_DATA,
      showAlbertaEnergyDaily: ENABLE_ALBERTA_ENERGY_DAILY,
      showAlbertaEnvironmental: ENABLE_ALBERTA_ENVIRONMENTAL,
      showBcEnvironmental: ENABLE_BC_ENVIRONMENTAL,
      showCompanyWhitelistManager:
        canQueryOwnCompanyWhitelists || canQueryCompanyWhitelists,
      showOrderReleaseLimitManager: isAdmin || canManageOrderReleaseLimits,
    }),
    [
      initialized,
      keycloak.authenticated,
      isAdmin,
      isApiManageMarks,
      isApiManageTrades,
      isApiManageExternalData,
      isApiApproveMarks,
      isApiApproveTrades,
      isTrader,
      isTraderAdmin,
      canQueryOwnCompanyWhitelists,
      canQueryCompanyWhitelists,
      canManageOrderReleaseLimits,
    ],
  );

  const login =
    keycloak.authenticated && keycloak.tokenParsed.preferred_username;
  const sidebarStorageKey = getSidebarStorageKey(userAccountId, login);
  const [isExpanded, setIsExpanded] = useState(() =>
    getStoredSidebarExpanded(sidebarStorageKey, window.innerWidth > 768),
  );
  const showMarketControl = permissions.showTradingAdmin;
  const isLightTheme =
    typeof document !== "undefined" &&
    document.body.classList.contains("light-theme");
  const expandedLogoSource = isLightTheme && NeutralLogo ? NeutralLogo : Logo;
  const navigationToggleLabel = t("ToggleNavigationView");

  const handleCloseAboutModel = useCallback(() => setShowAboutModal(false), []);
  const handleShowAboutModel = useCallback(() => setShowAboutModal(true), []);

  // Mobile resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setIsExpanded(getStoredSidebarExpanded(sidebarStorageKey, !isMobile));
  }, [sidebarStorageKey, isMobile]);

  useEffect(() => {
    if (!sidebarStorageKey) {
      return;
    }

    try {
      localStorage.setItem(sidebarStorageKey, String(isExpanded));
    } catch (error) {
      console.warn("Failed to save sidebar state:", error);
    }
  }, [sidebarStorageKey, isExpanded]);

  useEffect(() => {
    if (isExpanded) {
      setIsSecondaryRailOpen(false);
    }
  }, [isExpanded]);

  const toggleSidebar = useCallback(() => {
    setIsExpanded((currentValue) => !currentValue);
  }, []);

  const handleNavLinkClick = useCallback(() => {
    // Collapse the sidebar on mobile after item selection
    if (isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  const runMarketControlAction = useCallback(
    async (action, successMessageKey) => {
      try {
        await action();
        toast.success(t(successMessageKey));
      } catch (ex) {
        showMarketActionErrors(ex, t);
      }
    },
    [t],
  );

  const handleOpenMarkets = useCallback(
    () => runMarketControlAction(openMarkets, "MarketsOpened"),
    [runMarketControlAction],
  );

  const handleCloseMarkets = useCallback(
    () => runMarketControlAction(closeMarkets, "MarketsClosed"),
    [runMarketControlAction],
  );

  const { topLevelItems, utilityItems } = useMemo(
    () =>
      buildNavigationModel({
        t,
        permissions,
        showMarketControl,
        handleOpenMarkets,
        handleCloseMarkets,
        handleShowAboutModel,
        login,
        unapprovedMarkCount,
        unapprovedTradeCount,
        handleLogout,
        subscribedMarkets,
        termSchedules,
        manageExternalMarkets,
      }),
    [
      handleCloseMarkets,
      handleLogout,
      handleOpenMarkets,
      handleShowAboutModel,
      login,
      manageExternalMarkets,
      permissions,
      showMarketControl,
      subscribedMarkets,
      t,
      termSchedules,
      unapprovedMarkCount,
      unapprovedTradeCount,
    ],
  );

  const { activeMenuItems, routeExpandedMenus } = useMemo(
    () =>
      deriveNavigationState({ topLevelItems, utilityItems }, location.pathname),
    [location.pathname, topLevelItems, utilityItems],
  );

  return (
    <div
      className={`sidebar ${!isExpanded ? "sidebar--dual-rail" : ""}`.trim()}
    >
      <MenuStateManager routeExpandedMenus={routeExpandedMenus}>
        {({
          expandedMenus,
          handleCollapseOthers,
          startNavigationSearchSession,
          commitNavigationSearchResult,
          endNavigationSearchSession,
          syncAutoExpandedMenus,
          toggleMenu,
        }) => (
          <SideNavContent
            t={t}
            isExpanded={isExpanded}
            isSecondaryRailOpen={isSecondaryRailOpen}
            setIsSecondaryRailOpen={setIsSecondaryRailOpen}
            expandedLogoSource={expandedLogoSource}
            navigationToggleLabel={navigationToggleLabel}
            toggleSidebar={toggleSidebar}
            handleNavLinkClick={handleNavLinkClick}
            handleOpenFlexTab={onMenuClick}
            activeMenuItems={activeMenuItems}
            topLevelItems={topLevelItems}
            utilityItems={utilityItems}
            iconLogo={IconLogo}
            isMobile={isMobile}
            expandedMenus={expandedMenus}
            handleCollapseOthers={handleCollapseOthers}
            startNavigationSearchSession={startNavigationSearchSession}
            commitNavigationSearchResult={commitNavigationSearchResult}
            endNavigationSearchSession={endNavigationSearchSession}
            syncAutoExpandedMenus={syncAutoExpandedMenus}
            toggleMenu={toggleMenu}
          />
        )}
      </MenuStateManager>

      <Modal show={showAboutModal} onHide={handleCloseAboutModel}>
        <Modal.Header closeButton>
          <Modal.Title>© Neutral Markets 2025</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <About />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SideNav;
